import { AsButton, isAuthorised, Spacer } from '@adserve/adserve-react-components';
import React, { useState, useEffect } from 'react';
import { GetBillingTabSummary, GetCampaignForwardSchedule, GetCampaignInvoiceHeaders, GetContactRoles, UpdateBillingPlanByCampaignId } from '../../../services/CampaignsDataService';
import BillingDisplayOptions from './BillingDisplayOptions';
import BillingForwardSchedule from './BillingForwardSchedule';
import BillingInvoicesIssued from './BillingInvoicesIssued';
import BillingPlanInfo from './BillingPlanInfo';
import FinancialPerformance from './FinancialPerformance';
import { pageContainerStyle, sectionedPanelStyle } from '../CampaignStyles';
import InvoiceDetails from './InvoiceDetails';
import ForwardScheduleDetails from './ForwardScheduleDetails';

const Billing = ({ campaign,
    changeMade,
    readOnly = false,
    contactRoles,
}) => {
    readOnly = readOnly || !isAuthorised('C_BILL_PLAN');
    const [apiParams, setApiParams] = useState({
        pageNumber: 1,
        pageSize: -1,
        campaignId: campaign?.id
    });
    const [showInvoices, setShowInvoices] = useState(true);
    const [showCreditNotes, setShowCreditNotes] = useState(true);
    const [invoiceHeaderData, setInvoiceHeaderData] = useState([]);
    const [forwardScheduleData, setForwardScheduleData] = useState([]);
    const [invoicesAirtime, setInvoicesAirtime] = useState(0);
    const [invoicesOffairs, setInvoicesOffairs] = useState(0);
    const [invoicesCommission, setInvoicesCommission] = useState(0);
    const [invoicesVat, setInvoicesVat] = useState(0);
    const [invoicesTotal, setInvoicesTotal] = useState(0);
    const [scheduledAirtime, setScheduledAirtime] = useState(0);
    const [scheduledOffairs, setScheduledOffairs] = useState(0);
    const [scheduledCommission, setScheduledCommission] = useState(0);
    const [scheduledVat, setScheduledVat] = useState(0);
    const [scheduledTotal, setScheduledTotal] = useState(0);
    const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState();
    const [showForwardScheduleDetails, setShowForwardScheduleDetails] = useState(false);
    const [forwardScheduleDetails, setForwardScheduleDetails] = useState();
    const [billingInfo, setBillingInfo] = useState([]);

    const loadInvoiceData = () => {
        GetCampaignInvoiceHeaders(apiParams, showInvoices, showInvoices, showCreditNotes)
            .then((data) => {
                setInvoiceHeaderData(data.data);
            })
            .catch(console.log);
    };

    const loadForwardScheduleData = () => {
        GetCampaignForwardSchedule({ id: campaign.id })
            .then((data) => {
                setForwardScheduleData(data);
            })
            .catch(console.log);
    };

    useEffect(() => {
        loadInvoiceData();
        loadForwardScheduleData();
    }, [showInvoices, showCreditNotes, apiParams]);

    useEffect(() => {
        GetBillingTabSummary(campaign?.id).then((data) => {
            let billingContactName = 'No Billing Contact';
            if (contactRoles) {
                const billingContactRole = contactRoles.find((x) => x.sortOrder === 7);
                if (billingContactRole) {
                    const bilingcontactDetails = campaign?.campaignContacts.find((x) => x.contactRoleId === billingContactRole?.id)
                    if (bilingcontactDetails) {
                        billingContactName = bilingcontactDetails?.contact?.fullName;
                    }
                }
            }
            data.billingContactName = billingContactName;
            setBillingInfo(data);
        }).catch((e) => console.log(e));
    }, [])

    const toggleShowInvoices = () => {
        setShowInvoices(!showInvoices);
    };

    const toggleShowCreditNotes = () => {
        setShowCreditNotes(!showCreditNotes);
    };

    useEffect(() => {
        let updatedLifetimeBilling = 0;
        let updatedYearBilling = 0;
        let updatedTotalScheduled = 0;
        let updatedInvoicesAirtime = 0;
        let updatedInvoicesOffairs = 0;
        let updatedInvoicesCommission = 0;
        let updatedInvoicesVat = 0;
        let updatedInvoicesTotal = 0;
        let updatedScheduledAirtime = 0;
        let updatedScheduledOffairs = 0;
        let updatedScheduledCommission = 0;
        let updatedScheduledVat = 0;
        let updatedScheduledTotal = 0;

        invoiceHeaderData?.forEach((invoice, i) => {
            updatedLifetimeBilling = updatedLifetimeBilling + invoice?.total;
            updatedYearBilling = updatedYearBilling + invoice?.total;
            updatedInvoicesAirtime = updatedInvoicesAirtime + invoice?.netAirtimeValue;
            updatedInvoicesOffairs = updatedInvoicesOffairs + invoice?.netOffAirsValue;
            updatedInvoicesCommission = updatedInvoicesCommission + invoice?.commission;
            updatedInvoicesVat = updatedInvoicesVat + invoice?.totalVat;
            updatedInvoicesTotal = updatedInvoicesTotal + invoice?.total;
        });

        setInvoicesAirtime(updatedInvoicesAirtime);
        setInvoicesOffairs(updatedInvoicesOffairs);
        setInvoicesCommission(updatedInvoicesCommission);
        setInvoicesVat(updatedInvoicesVat);
        setInvoicesTotal(updatedInvoicesTotal);

        if (forwardScheduleData) {
            forwardScheduleData?.forEach((fs, i) => {
                updatedTotalScheduled = updatedTotalScheduled + fs?.total;
                updatedScheduledAirtime = updatedScheduledAirtime + fs?.airtimeValue;
                updatedScheduledOffairs = updatedScheduledOffairs + fs?.offAirValue;
                updatedScheduledCommission = updatedScheduledCommission + fs?.comission;
                updatedScheduledVat = updatedScheduledVat + fs?.totalVat;
                updatedScheduledTotal = updatedScheduledTotal + fs?.total;
            });
        }
        setScheduledAirtime(updatedScheduledAirtime);
        setScheduledOffairs(updatedScheduledOffairs);
        setScheduledCommission(updatedScheduledCommission);
        setScheduledVat(updatedScheduledVat);
        setScheduledTotal(updatedScheduledTotal);
    }, [invoiceHeaderData?.length]);

    const handleBillingPlanUpdate = async () => {
        await UpdateBillingPlanByCampaignId(campaign?.id);
        loadForwardScheduleData();
    };

    return (
        <div style={pageContainerStyle}>
            <div style={sectionedPanelStyle}>
                <div
                    style={{
                        display: 'flex',
                        marginLeft: 24,
                        marginTop: 24
                    }}
                >
                    <BillingPlanInfo 
                        campaign={campaign}
                        billingInfo={billingInfo}
                        forwardScheduleData={forwardScheduleData}
                    />
                    <div
                        style={{
                            marginLeft: 103,
                            marginTop: -11,
                            marginRight: 20
                        }}
                    >
                        <FinancialPerformance campaign={campaign} />
                    </div>
                </div>
            </div>

            <Spacer height={16} width={0} />

            {!showInvoiceDetails && !showForwardScheduleDetails && (
                <>
                    <div style={sectionedPanelStyle}>
                        <div
                            style={{
                                fontSize: 15,
                                fontWeight: 500,
                                marginTop: 11,
                                marginBottom: 12,
                                marginLeft: 24
                            }}
                        >
                            {window.translate('Invoices Issued')}
                        </div>
                        <div style={{ marginLeft: 1525, marginTop: -29 }}>
                            <BillingDisplayOptions
                                changeMade={changeMade}
                                campaign={campaign}
                                showInvoices={showInvoices}
                                showCreditNotes={showCreditNotes}
                                toggleShowInvoices={toggleShowInvoices}
                                toggleShowCreditNotes={toggleShowCreditNotes}
                            />
                        </div>

                        <div style={{ marginLeft: 23, marginTop: 8 }}>
                            <BillingInvoicesIssued
                                campaign={campaign}
                                tableData={invoiceHeaderData}
                                invoicesAirtime={invoicesAirtime}
                                invoicesOffairs={invoicesOffairs}
                                invoicesCommission={invoicesCommission}
                                invoicesVat={invoicesVat}
                                invoicesTotal={invoicesTotal}
                                setShowInvoiceDetails={setShowInvoiceDetails}
                                setInvoiceDetails={setInvoiceDetails}
                            />
                        </div>
                    </div>
                    <Spacer height={16} width={0} />

                    <div style={{ ...sectionedPanelStyle, height: 440 }}>
                        <div
                            style={{
                                fontSize: 15,
                                fontWeight: 500,
                                marginTop: 11,
                                marginBottom: 12,
                                marginLeft: 24
                            }}
                        >
                            {window.translate('Forward Schedule')}
                        </div>
                        <div 
                            style={{ 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                marginRight: 20,
                                marginTop: -25 
                            }}
                        >
                            <AsButton
                                id='update-billing-plan'
                                handleClick={handleBillingPlanUpdate}
                                disabled={readOnly}
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: 15,
                                    height: 30,
                                    width: 80,
                                    marginLeft: 20,
                                    marginBottom: 10
                                }}
                            >
                                {window.translate('Refresh')}
                            </AsButton>
                        </div>
                        <div style={{ marginLeft: 23, marginTop: 8 }}>
                            <BillingForwardSchedule
                                campaign={campaign}
                                tableData={forwardScheduleData}
                                scheduledAirtime={scheduledAirtime}
                                scheduledOffairs={scheduledOffairs}
                                scheduledCommission={scheduledCommission}
                                scheduledVat={scheduledVat}
                                scheduledTotal={scheduledTotal}
                                setShowForwardScheduleDetails={setShowForwardScheduleDetails}
                                setForwardScheduleDetails={setForwardScheduleDetails}
                                loadData={loadForwardScheduleData}
                                readOnly={readOnly}
                            />
                        </div>
                    </div>
                    <Spacer height={25.5} width={0} />
                </>
            )}

            {showInvoiceDetails && (
                <InvoiceDetails
                    sourceName={campaign?.name}
                    payload={invoiceDetails}
                    readOnly={readOnly}
                    setShowInvoiceDetails={setShowInvoiceDetails}
                />
            )}

            {showForwardScheduleDetails && (
                <ForwardScheduleDetails
                    sourceName={campaign?.name}
                    payload={forwardScheduleDetails}
                    setShowForwardScheduleDetails={setShowForwardScheduleDetails}
                />
            )}
        </div>
    );
};

export default Billing;
